import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { api, clientsApi } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatDakar, timeAgo } from "@/lib/time";
import {
  User, Phone, MapPin, Clock, Crosshair, Video, VideoOff, Loader2, StickyNote,
  UserRound, Mail, Home, LifeBuoy, Route, UserCheck, Timer,
} from "lucide-react";

const statusMeta = (s) => {
  const v = (s || "").toLowerCase();
  if (v.startsWith("cancel")) return { label: "Annulé", cls: "bg-red-500/10 text-red-400 border-red-500/30" };
  if (v === "resolved") return { label: "Traitée", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" };
  if (v === "active" || v === "") return { label: "En cours", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" };
  return { label: s || "Terminée", cls: "bg-slate-500/10 text-slate-300 border-slate-600" };
};

const RouteMap = ({ locations }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const pts = (locations || []).filter((l) => l?.lat != null).map((l) => [l.lat, l.lng]);
    const center = pts[pts.length - 1] || [14.6, -14.4];
    const map = L.map(containerRef.current, {
      center, zoom: pts.length ? 14 : 6, zoomControl: true, attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, subdomains: "abc", className: "osm-dark-tiles",
    }).addTo(map);
    if (pts.length > 1) {
      L.polyline(pts, { color: "#EF4444", weight: 4, opacity: 0.95, dashArray: "1,8", lineCap: "round" }).addTo(map);
    }
    if (pts.length) {
      const s = pts[0], e = pts[pts.length - 1];
      L.marker(s, { icon: L.divIcon({ className: "", html: '<div class="path-start-marker"></div>', iconSize: [14, 14], iconAnchor: [7, 7] }) })
        .addTo(map).bindTooltip("Départ", { direction: "top", offset: [0, -8] });
      L.marker(e, { icon: L.divIcon({ className: "", html: '<div class="sos-marker"><div class="ring"></div><div class="dot"></div></div>', iconSize: [20, 20], iconAnchor: [10, 10] }) })
        .addTo(map).bindTooltip("Dernière position", { direction: "top", offset: [0, -12] });
      if (pts.length > 1) map.fitBounds(L.latLngBounds(pts).pad(0.3), { maxZoom: 16, animate: false });
    }
    mapRef.current = map;
    setTimeout(() => { try { map.invalidateSize(); } catch (e) { /* no-op */ } }, 250);
    return () => { try { map.stop(); map.remove(); } catch (e) { /* no-op */ } mapRef.current = null; };
  }, [locations]);

  return <div ref={containerRef} data-testid="history-route-map" className="w-full h-60 rounded-xl overflow-hidden border border-slate-800 bg-slate-950" />;
};

const Row = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-slate-800/80 last:border-0">
    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${accent || "text-slate-500"}`} />
    <div className="min-w-0">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-slate-200 break-words">{value}</p>
    </div>
  </div>
);

const HistoryDetail = ({ alertId, open, onClose }) => {
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoState, setVideoState] = useState("idle");
  const videoUrlRef = useRef(null);

  useEffect(() => {
    if (!open || !alertId) { setAlert(null); return; }
    setLoading(true);
    api.getAlert(alertId)
      .then((a) => setAlert(a))
      .catch(() => setAlert(null))
      .finally(() => setLoading(false));
  }, [open, alertId]);

  useEffect(() => {
    setClient(null);
    if (alert?.user_phone) {
      clientsApi.byPhone(alert.user_phone).then((c) => setClient(c || null)).catch(() => setClient(null));
    }
  }, [alert?.user_phone, alert?.alert_id]);

  useEffect(() => {
    const revoke = () => { if (videoUrlRef.current) { URL.revokeObjectURL(videoUrlRef.current); videoUrlRef.current = null; } };
    revoke();
    setVideoUrl(null);
    if (alert?.has_video && alert?.video_path) {
      let cancelled = false;
      setVideoState("loading");
      api.getFileBlob(alert.video_path)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          videoUrlRef.current = url;
          setVideoUrl(url);
          setVideoState("ready");
        })
        .catch(() => { if (!cancelled) setVideoState("error"); });
      return () => { cancelled = true; revoke(); };
    }
    setVideoState("idle");
    return revoke;
  }, [alert?.alert_id, alert?.has_video, alert?.video_path]);

  const loc = alert?.current_location;
  const meta = statusMeta(alert?.status);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="history-detail-modal"
        className="max-w-3xl p-0 gap-0 bg-[#0B1120] border-slate-800 text-slate-100 overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-950 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-display font-bold text-slate-100 truncate">
              {alert?.user_name || "Détail de l'alerte"}
            </DialogTitle>
            <DialogDescription className="sr-only">Détail de l'alerte SOS : trajet GPS, vidéo transmise, fiche client et notes d'intervention.</DialogDescription>
            <p className="text-[11px] font-mono text-slate-500">
              {alert?.triggered_at ? `${formatDakar(alert.triggered_at)} (Dakar)` : "Historique SOS"}
            </p>
          </div>
          {alert && (
            <span className={`text-[11px] font-mono border px-2.5 py-1 rounded-full shrink-0 mr-6 ${meta.cls}`}>{meta.label}</span>
          )}
        </div>

        {loading || !alert ? (
          <div className="flex items-center justify-center h-64 text-slate-500" data-testid="history-detail-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="max-h-[75vh] overflow-y-auto p-5 space-y-5">
            {/* GPS route map */}
            <div className="space-y-2">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Route className="w-3.5 h-3.5 text-red-400" /> Trajet GPS — {(alert.locations || []).length} point(s)
              </p>
              <RouteMap locations={alert.locations} />
            </div>

            {/* GPS / alert info */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-xl px-4">
              <Row icon={Phone} label="Téléphone" value={<a href={`tel:${alert.user_phone}`} className="text-emerald-400 hover:underline" data-testid="history-detail-phone">{alert.user_phone || "—"}</a>} accent="text-emerald-400" />
              <Row icon={MapPin} label="Dernière position GPS" value={loc ? `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}` : "—"} accent="text-yellow-400" />
              <Row icon={Crosshair} label="Précision" value={loc?.accuracy != null ? `± ${loc.accuracy} m` : "—"} />
              <Row icon={Clock} label="Déclenchée" value={alert.triggered_at ? `${formatDakar(alert.triggered_at)} (${timeAgo(alert.triggered_at)})` : "—"} />
              <Row icon={Timer} label={(alert.status || "").toLowerCase().startsWith("cancel") ? "Annulée" : "Clôturée"} value={alert.ended_at ? formatDakar(alert.ended_at) : "—"} />
              <Row icon={UserCheck} label="Agent affecté" value={alert.assigned_to || "Aucun agent affecté"} accent={alert.assigned_to ? "text-amber-400" : "text-slate-500"} />
            </div>

            {/* Client record */}
            {client ? (
              <div data-testid="history-detail-client" className="bg-emerald-950/20 border border-emerald-500/30 rounded-xl p-4 space-y-2.5">
                <p className="text-xs font-mono uppercase tracking-wider text-emerald-400 flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5" /> Fiche client enregistrée</p>
                <p className="text-sm text-slate-100 font-semibold">{client.first_name} {client.last_name}</p>
                <div className="space-y-1.5 text-sm text-slate-300">
                  {client.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" /> {client.email}</div>}
                  {client.address && <div className="flex items-center gap-2"><Home className="w-3.5 h-3.5 text-slate-500 shrink-0" /> {client.address}</div>}
                  {(client.emergency_contact_name || client.emergency_contact_phone) && (
                    <div className="flex items-start gap-2 text-amber-300 pt-1 border-t border-emerald-500/20 mt-2">
                      <LifeBuoy className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>Contact d'urgence : <strong>{client.emergency_contact_name || "—"}</strong>{client.emergency_contact_phone && <> · <a href={`tel:${client.emergency_contact_phone}`} className="underline">{client.emergency_contact_phone}</a></>}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div data-testid="history-detail-client-none" className="text-xs text-slate-500 flex items-center gap-1.5 bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2">
                <UserRound className="w-3.5 h-3.5" /> Client non enregistré dans le fichier.
              </div>
            )}

            {/* Transmitted video */}
            <div className="space-y-2">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Video className="w-3.5 h-3.5" /> Vidéo transmise
              </p>
              {!alert.has_video ? (
                <div data-testid="history-detail-video-none" className="flex items-center gap-2 text-sm text-slate-500 bg-slate-900/80 border border-slate-800 rounded-xl p-4">
                  <VideoOff className="w-4 h-4" /> Aucune vidéo transmise pour cette alerte.
                </div>
              ) : videoState === "loading" ? (
                <div data-testid="history-detail-video-loading" className="flex items-center gap-2 text-sm text-slate-400 bg-slate-900/80 border border-slate-800 rounded-xl p-4">
                  <Loader2 className="w-4 h-4 animate-spin" /> Chargement de la vidéo...
                </div>
              ) : videoState === "error" ? (
                <div data-testid="history-detail-video-error" className="flex items-center gap-2 text-sm text-red-400 bg-red-950/30 border border-red-500/30 rounded-xl p-4">
                  <VideoOff className="w-4 h-4" /> Impossible de charger la vidéo.
                </div>
              ) : videoUrl ? (
                <video data-testid="history-detail-video" src={videoUrl} controls playsInline className="w-full rounded-xl border border-slate-800 bg-black max-h-80" />
              ) : null}
            </div>

            {/* Intervention notes */}
            <div className="space-y-2">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Notes d'intervention</p>
              {(alert.notes || []).length > 0 ? (
                <div className="space-y-2" data-testid="history-detail-notes">
                  {alert.notes.map((n) => (
                    <div key={n.note_id} className="bg-slate-900/80 border border-slate-800 rounded-lg p-3">
                      <p className="text-sm text-slate-200">{n.text}</p>
                      <p className="text-[10px] font-mono text-slate-500 mt-1">{n.author} · {timeAgo(n.ts)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div data-testid="history-detail-notes-none" className="text-xs text-slate-500 bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2">
                  Aucune note d'intervention pour cette alerte.
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HistoryDetail;
